import { useState, useEffect, useRef } from "react";
import Select, { components } from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { listServices } from "../../actions/serviceActions";
import {
  white,
  textPrimary,
  textSecondary,
  newBorder,
  primary,
  backgroundLight,
  color8,
  hoverItemColor,
  primaryAlt,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import { Flex, Box, Text, Spinner, Icon, Button } from "@chakra-ui/react";
import { TbDental } from "react-icons/tb";
import { motion } from "framer-motion";
// import DropdownButtonNew from "../buttons/DropdownButtonNew";
import { listServiceGroups } from "../../actions/serviceGroupActions";
import DropdownButtonAlt from "../buttons/DropdownButtonAlt";
import SearchInput from "../input/SearchInput";
import { FaLock, FaLockOpen } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import useCurrencySymbol from "../../hooks/useCurrencySymbol";
import useIsMobile from "../../hooks/useIsMobile";

const ServiceSelectNew = ({
  onChange,
  isAdvancedMode,
  toggleAdvancedMode,
  currency,
}) => {
  const { slug } = useParams();
  const isMobile = useIsMobile();
  // TRANSLATION
  const { t } = useTranslation();

  // REDUX
  const dispatch = useDispatch();

  const serviceList = useSelector((state) => state.serviceList);
  const {
    loading: loadingServices,
    error: errorServices,
    services,
  } = serviceList;

  // REDUX
  const serviceGroupList = useSelector((state) => state.serviceGroupList);
  const { serviceGroups } = serviceGroupList;

  // CONSTANTS

  // const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState(null);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [selectedServiceGroup, setSelectedServiceGroup] = useState(""); // State for selected service group
  const [customSearch, setCustomSearch] = useState(""); // State for custom search
  const [allServiceGroups, setAllServiceGroups] = useState([]); // State for all service groups

  // Custom Search Handler
  const handleCustomSearchChange = (e) => {
    setCustomSearch(e.target.value);
  };

  // Filter services based on custom search
  // Filter services based on selected service group
  const filteredServices = serviceOptions.map((group) => ({
    ...group,
    options:
      selectedServiceGroup === "" ||
      group.options[0].serviceGroup._id === selectedServiceGroup
        ? group.options.filter((service) =>
            service.label.toLowerCase().includes(customSearch.toLowerCase())
          )
        : [],
  }));

  const bottomRef = useRef(null);

  // HANDLERS
  const handleChange = (selectedOption) => {
    if (onChange) {
      onChange(selectedOption);
    }
    // Reset the value of the select after a change
    setValue(null);
  };

  useEffect(() => {
    dispatch(
      listServices(
        slug,
        "",
        1,
        "",
        "",
        "",
        true,
        "",
        "",
        "",
        "",
        "",
        selectedServiceGroup
      ) // Pass selectedServiceGroup as the serviceGroup parameter
    );
  }, [dispatch, selectedServiceGroup]);

  // USE EFFECT LOAD SERVICE GROUPS
  useEffect(() => {
    // Dispatch the action with fetchAll set to true
    dispatch(listServiceGroups(slug, "", "", "", "", "", true));
  }, [dispatch]);

  useEffect(() => {
    if (serviceGroups) {
      const activeServiceGroups = serviceGroups.filter(
        (group) => group.status !== "inactive"
      );

      // Add the "All Services" option
      const allServicesOption = { value: "", label: t("service.allServices") };
      const transformedServiceGroups = [
        allServicesOption,
        ...activeServiceGroups.map((group) => ({
          value: group._id,
          label: group.name,
          status: group.status,
        })),
      ];

      setAllServiceGroups(transformedServiceGroups);
    }
  }, [serviceGroups]);

  // USE EFFECT TRANSFORM SERVICES FOR REACT SELECT AND SET OPTIONS, EXCLUDE INACTIVE SERVICES
  useEffect(() => {
    if (!loadingServices && Array.isArray(services) && services.length > 0) {
      // Filter out inactive services
      const activeServices = services.filter(
        (service) => service.status !== "inactive"
      );

      const groupedOptions = activeServices.reduce((groups, service) => {
        const groupLabel = service.serviceGroup.name;
        if (!groups[groupLabel]) {
          groups[groupLabel] = [];
        }
        groups[groupLabel].push({
          value: service._id,
          label: service.name,
          image: service.image,
          price: service.price,
          maxPrice: service.maxPrice,
          priceRange: service.priceRange,
          priceVariations: service.priceVariations,
          defaultPublicReimbursementCode:
            service.defaultPublicReimbursementCode,
          serviceGroup: service.serviceGroup,
          customId: service.customId,
          status: service.status,
        });
        return groups;
      }, {});

      setServiceOptions(
        Object.keys(groupedOptions).map((groupLabel) => ({
          label: groupLabel,
          options: groupedOptions[groupLabel],
        }))
      );
    }

    // if (!loadingServices && services.length > 0) {
    //   // Scroll to the bottom of the page after setting service options
    //   bottomRef.current.scrollIntoView({ behavior: "smooth" });
    // }
  }, [loadingServices, services]);

  const currencySymbol = useCurrencySymbol(currency);

  // Custom Option component to display the image
  const CustomOption = (props) => {
    return (
      <components.Option {...props}>
        <Flex
          align="center"
          w="100%"
          p={["0.5rem 1.5rem", "0.5rem 1.5rem"]}
          role="group"
          _hover={{
            bg: hoverItemColor,
            color: textPrimary,
            cursor: "pointer",
          }}
          borderBottom={`1px solid ${newBorder}`}
        >
          <Flex align="center" justify="space-between" w="100%">
            <Flex align="baseline">
              <Text
                color={textPrimary}
                _groupHover={{
                  color: textPrimary,
                }}
                fontWeight="500"
                fontSize="sm"
                mr={2}
              >
                {props.data.label}
                {props.data.priceVariations && (
                  <Icon
                    as={TbDental}
                    color={color8}
                    position="relative"
                    top="2px"
                    fontSize="0.875rem"
                    ml="0.25rem"
                  />
                )}
              </Text>
            </Flex>
            <Flex
              align="center"
              justify="center"
              p="0.5rem 0.75rem"
              bg="transparent"
              borderRadius="10rem"
              _groupHover={{
                bg: "transparent",
              }}
            >
              {!props.data.priceRange ? (
                <Text
                  color={textSecondary}
                  fontSize="0.8rem"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  _groupHover={{
                    color: textPrimary,
                  }}
                >
                  {currencySymbol} {props.data.price}
                </Text>
              ) : (
                <Text
                  color={textSecondary}
                  fontSize="0.8rem"
                  fontWeight="500"
                  whiteSpace="nowrap"
                  _groupHover={{
                    color: textPrimary,
                  }}
                >
                  {props.data.price} - {currencySymbol} {props.data.maxPrice}
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </components.Option>
    );
  };

  // Make sure service groups remain constant
  const handleServiceGroupChange = (groupId) => {
    setSelectedServiceGroup(groupId); // Update selected service group
  };

  // console.log("Services", services);
  // console.log("Service Options", serviceOptions);
  // console.log("Service Group Options", serviceGroupOptions);
  // console.log("All Service Groups", allServiceGroups);
  // console.log("Selected Service Group", selectedServiceGroup);
  // console.log("Filtered Services", filteredServices);

  return (
    <>
      {loadingServices ? (
        <Flex w="100%" align="center" justify="center" p="3rem 0">
          <Spinner size="lg" color={primary} />
        </Flex>
      ) : (
        <Box
          w="100%"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: 0.2 }}
        >
          <Flex
            w="100%"
            align={["center", "center"]}
            justify={["center", "space-between"]}
            gap="1rem"
            direction={["column", "row"]}
            p={["0 1.5rem", "initial"]}
          >
            <SearchInput
              placeholder={t("service.search")}
              onChange={handleCustomSearchChange}
              searchName={customSearch}
              p="0"
              m="0"
            />
            {/* <Flex align="center" w="100%">
              <Input
                size="md"
                fontSize="sm"
                placeholder={t("service.search")}
                value={customSearch}
                onChange={handleCustomSearchChange}
                width="100%"
                borderRadius="10rem"
              />
            </Flex> */}
            <Flex align="center" w={["100%", "initial"]}>
              <DropdownButtonAlt
                isFullWidth
                size="lg"
                initialLabel={
                  selectedServiceGroup
                    ? allServiceGroups.find(
                        (item) => item.value === selectedServiceGroup
                      )?.label
                    : t("service.allServices")
                }
                items={allServiceGroups.map((group) => group.label)}
                onSelectItem={(label) => {
                  const selectedGroup = allServiceGroups.find(
                    (item) => item.label === label
                  );
                  handleServiceGroupChange(
                    selectedGroup ? selectedGroup.value : ""
                  );
                }}
              />
            </Flex>
          </Flex>

          {isMobile && (
            <Flex
              w="100"
              align="center"
              m="1.5rem 0 0"
              p={["0 1.5rem", "initial"]}
            >
              <Button
                size="sm"
                maxW="2rem"
                bg={isAdvancedMode ? primaryAlt : backgroundLight}
                color={isAdvancedMode ? white : textSecondary}
                onClick={toggleAdvancedMode}
                borderRadius="10rem"
                _hover={{
                  bg: isAdvancedMode ? primaryAlt : backgroundLight,
                  color: isAdvancedMode ? white : textSecondary,
                }}
              >
                <Icon
                  as={isAdvancedMode ? FaLock : FaLockOpen}
                  fontSize="0.75rem"
                  pointerEvents="none"
                />
              </Button>
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                ml="0.5rem"
                fontWeight="500"
              >
                {isAdvancedMode
                  ? t("service.advancedModeEnabled")
                  : t("service.advancedModeDisabled")}
              </Text>
            </Flex>
          )}

          <Box
            w="100%"
            minH={["calc(330px + 0rem)", "calc(369px + 0rem)"]}
            m={["2rem 0 0", "initial"]}
            // borderTop={[`1px solid ${newBorder}`, "none"]}
            borderBottom={[`1px solid ${newBorder}`, "none"]}
          >
            {isMobile && (
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color={textPrimary}
                p="1rem 1.5rem 0"
              >
                {t("service.services")}
              </Text>
            )}
            <Select
              menuIsOpen
              autoFocus
              width="100%"
              onChange={handleChange}
              // isSearchable
              value={services.find((option) => option.value === value) || null}
              // onChange={onChange}
              options={filteredServices}
              placeholder={t("treatmentPlan.searchService")}
              noOptionsMessage={() => (
                <Flex p="3rem 1rem" align="center" justify="center">
                  <Text fontSize="sm" fontWeight="500" color={textSecondary}>
                    {t("service.noServicesInSearch")}
                  </Text>
                </Flex>
              )} // Custom message
              components={{ Option: CustomOption }}
              styles={{
                menu: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: isMobile ? "0" : "1rem",
                  overflow: "hidden",
                  border: isMobile
                    ? `0px solid ${newBorder}`
                    : `1px solid ${newBorder}`,
                  position: "relative",
                  //  boxShadow: `0 0 0 1px ${newBorder}`,
                  boxShadow: "none",
                  zIndex: "0",
                  padding: "0",
                  margin: "1rem 0 0",
                  color: textPrimary,
                }),

                menuList: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0rem 0rem 0",
                  maxHeight: isMobile ? "330px" : "369px",
                }),
                group: (baseStyles) => ({
                  ...baseStyles,
                  padding: "0",
                  margin: "0",
                }),
                groupHeading: (baseStyles) => ({
                  ...baseStyles,
                  fontSize: "11px",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.369px",
                  backgroundColor: backgroundLight,
                  borderBottom: `1px solid ${newBorder}`,
                  padding: isMobile ? "0.75rem 1.5rem" : "0.75rem 1.5rem",
                  margin: "0",
                  color: textSecondary,
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  fontSize: "0.875rem",

                  padding: "0px 0px",
                  borderBottom: `0px solid ${newBorder}`,
                  margin: "0",
                  backgroundColor: state.hover ? "white" : "white",
                }),

                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? newBorder : newBorder,
                  color: textSecondary,
                  borderRadius: "10rem",
                  backgroundColor: `${white}`,
                  border: `1px solid ${newBorder}`,
                  fontSize: isMobile ? "1rem" : "0.938rem",
                  fontWeight: "500",
                  height: "3rem",
                  width: "100%",
                  minWidth: isMobile ? "100%" : "500px",
                  padding: "0 0.25rem",
                  display: "none",
                  ":focus-visible": {
                    borderColor: primary,
                    boxShadow: `0 0 0 1px ${newBorder}`,
                  },
                }),

                singleValue: (baseStyles, provided, state) => {
                  const opacity = state.isDisabled ? 0.5 : 1;
                  const transition = "opacity 300ms";

                  return {
                    ...provided,
                    opacity,
                    transition,
                    color:
                      state.data.value === "" ? "desired color" : textPrimary,
                  };
                },
              }}
            />
          </Box>

          {!isMobile && (
            <Flex
              w="100"
              align="center"
              m="1.5rem 0 0"
              p={["0 1.5rem", "initial"]}
            >
              <Button
                size="sm"
                maxW="2rem"
                bg={isAdvancedMode ? primaryAlt : backgroundLight}
                color={isAdvancedMode ? white : textSecondary}
                onClick={toggleAdvancedMode}
                borderRadius="10rem"
                _hover={{
                  bg: isAdvancedMode ? primaryAlt : backgroundLight,
                  color: isAdvancedMode ? white : textSecondary,
                }}
              >
                <Icon
                  as={isAdvancedMode ? FaLock : FaLockOpen}
                  fontSize="0.75rem"
                  pointerEvents="none"
                />
              </Button>
              <Text
                fontSize="0.8rem"
                color={textSecondary}
                ml="0.5rem"
                fontWeight="500"
              >
                {isAdvancedMode
                  ? t("service.advancedModeEnabled")
                  : t("service.advancedModeDisabled")}
              </Text>
            </Flex>
          )}

          {/* Scroll to bottom reference */}

          <div ref={bottomRef} />
        </Box>
      )}
    </>
  );
};

export default ServiceSelectNew;
